import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import './ProfileAvatar.css';

const ProfileAvatar = ({
  src,
  alt = 'Profile',
  size = 'medium', // 'small', 'medium', 'large', 'xlarge'
  className = '',
  showBorder = false,
  showVerifiedBadge = false,
  isVerified = false,
  onClick = null
}) => {
  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🖼️ ProfileAvatar received src:', src);
  }
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'profile-avatar-small';
      case 'medium':
        return 'profile-avatar-medium';
      case 'large':
        return 'profile-avatar-large';
      case 'xlarge':
        return 'profile-avatar-xlarge';
      default:
        return 'profile-avatar-medium';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small':
        return 'w-4 h-4';
      case 'medium':
        return 'w-6 h-6';
      case 'large':
        return 'w-8 h-8';
      case 'xlarge':
        return 'w-12 h-12';
      default:
        return 'w-6 h-6';
    }
  };

  const containerClasses = [
    'profile-avatar',
    getSizeClasses(),
    showBorder ? 'with-border' : '',
    onClick ? 'clickable' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="avatar-image"
          onLoad={(e) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('🖼️ ProfileAvatar: Image loaded successfully:', src);
            }
          }}
          onError={(e) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('🖼️ ProfileAvatar: Image failed to load:', src);
            }
            // Hide image and show fallback if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}

      {/* Fallback icon - always rendered but hidden if image loads */}
      <div
        className="avatar-fallback"
        style={{ display: src ? 'none' : 'flex' }}
      >
        <UserIcon className={`avatar-icon ${getIconSize()}`} />
      </div>

      {/* Verified badge */}
      {showVerifiedBadge && isVerified && (
        <div className="verified-badge">
          <svg 
            className="verified-icon" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path 
              fillRule="evenodd" 
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
              clipRule="evenodd" 
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default ProfileAvatar;
